import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import type { Translations } from '@change-corgi/core/i18n';
import { I18nOverrides } from '@change-corgi/core-react/i18n';

import type { ErrorStateDetails } from '#src/app/shared/utils/async';
import { isError, isLoaded } from '#src/app/shared/utils/async';

import type {
	PetitionDetailsPageCombinedState,
	PetitionDetailsPagePrefetchedContext,
	PetitionDetailsPagePrefetchedUserContext,
} from './hook';
import { usePageContext } from './hook';
import { PetitionDetailsPageDataContextProvider } from './pageData';
import { PetitionDetailsPageStateContextProvider } from './pageState';
import { PetitionDetailsPageUserDataContextProvider } from './pageUserData';

export type ErrorHandlerProps = {
	context: ErrorStateDetails<PetitionDetailsPageCombinedState> | { error: 'user_info_load_failure'; cause?: unknown };
};

type Props = {
	slug: string;
	prefetchedData: PetitionDetailsPagePrefetchedContext | undefined;
	prefetchedUserData: PetitionDetailsPagePrefetchedUserContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

// Stable identity so `control` (and any other non-overridden variation) doesn't defeat
// I18nOverrides's `[utilityContext, translations]` memo on every render — a fresh `{}` literal here
// would force every consumer in the subtree to re-render for all traffic while the FC is off.
const NO_SHARE_CTA_COPY_OVERRIDES: Translations = {};

export const PetitionDetailsPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	prefetchedUserData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(slug, prefetchedData, prefetchedUserData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageUserData } = pageContextState;

	// control needs no override — it renders the existing, unchanged fe.components.petition_share.action
	// key (see docs/packages/experiments/i18n-copy.md § "FC: multiple variants").
	const shareCtaCopyVariation = pageUserData.data.experiments.petitionDetailsShareCtaCopyExperiment.variation;
	const shareCtaCopyTranslations =
		shareCtaCopyVariation === 'variant_1' || shareCtaCopyVariation === 'variant_2'
			? pageData.data.featureConfigs.petitionDetailsShareCtaCopyLabels[shareCtaCopyVariation]
			: NO_SHARE_CTA_COPY_OVERRIDES;

	return (
		<PetitionDetailsPageDataContextProvider pageData={pageData}>
			<PetitionDetailsPageUserDataContextProvider pageUserData={pageUserData}>
				<I18nOverrides translations={shareCtaCopyTranslations}>
					<PetitionDetailsPageStateContextProvider>{children}</PetitionDetailsPageStateContextProvider>
				</I18nOverrides>
			</PetitionDetailsPageUserDataContextProvider>
		</PetitionDetailsPageDataContextProvider>
	);
};
